'use client';

import { createContext, ReactNode } from 'react';
import { Position } from '@headless-workspace/config';
import { FunctionHelpers } from '@headless-workspace/utils';

export type SideMenuModalMap = { default: ReactNode } & Record<string, ReactNode>;

export type SideMenuModal = {
    side: Position;
    isOpen: boolean;
    map: SideMenuModalMap;
    className?: string;
    zIndex?: number;
    removeOnClose?: boolean;
    navigationStack: string[];
    onClose?: () => void;
};

type SideMenuModalArgs = {
    side: Position;
    isOpen?: boolean;
    map: SideMenuModalMap;
    className?: string;
    removeOnClose?: boolean;
    onClose?: () => void;
};

export const SideMenuModal = ({
    side,
    map,
    className,
    isOpen = false,
    removeOnClose,
    onClose,
}: SideMenuModalArgs): SideMenuModal => ({
    side,
    map,
    isOpen,
    className,
    removeOnClose,
    navigationStack: [],
    onClose,
});

export type SideMenuModalList = Record<string, SideMenuModal>;

export type ModalActionsContextType = {
    /**
     * Open a menuModal
     * @param id the id of the menuModal
     * @param content sets the default content of the menuModal (optional if the content is already set)
     */
    open: (id: string, content?: ReactNode) => void;
    /**
     * Add a menuModal
     * @param id the id of the menuModal
     * @param menuModal the menuModal to add
     */
    add: (id: string, menuModal: SideMenuModal) => void;
    /**
     * Add a menuModal and open it
     * @param id the id of the menuModal
     * @param menuModal the menuModal to add
     */
    addAndOpen: (id: string, menuModal: SideMenuModal) => void;
    /**
     * Add a menuModal if it doesn't exist and open it
     * @param id the id of the menuModal
     * @param menuModal the menuModal to add
     * @param override if true, override the menuModal if it exists
     */
    addIfAbsentAndOpen: (id: string, menuModal: SideMenuModal, override?: boolean) => void;
    /**
     * Close a menuModal
     * @param id the id of the menuModal
     * @param remove if true, remove the menuModal from the list
     */
    close: (id: string, remove?: boolean) => void;
    /**
     * Navigate to a menuModal
     * @param menuModalId the id of the menuModal
     * @param contentId the id of the content
     * @param content sets the content of the contentId (optional if the content is already set)
     */
    navigate: (menuModalId: string, contentId: string, content?: ReactNode) => void;
    /**
     * Go back to the previous menuModal
     * @param id the id of the menuModal
     */
    goBack: (id: string) => void;
    /**
     * Hydrate the menuModal list
     * @param menuModals the list of menuModals
     */
    hydrate: (menuModals: SideMenuModalList) => void;
};

export type ModalStateContextType = {
    sideMenuModalList: SideMenuModalList;
    isOpen: boolean;
    modalIdInView?: string;
    isMenuModalOpen: (id: string) => boolean;
};

const modalActionsContextInitialValues: ModalActionsContextType = {
    open: FunctionHelpers.emptyFn,
    add: FunctionHelpers.emptyFn,
    addAndOpen: FunctionHelpers.emptyFn,
    addIfAbsentAndOpen: FunctionHelpers.emptyFn,
    close: FunctionHelpers.emptyFn,
    navigate: FunctionHelpers.emptyFn,
    goBack: FunctionHelpers.emptyFn,
    hydrate: FunctionHelpers.emptyFn,
};

export const ModalActionsContext = createContext<ModalActionsContextType>(modalActionsContextInitialValues);
ModalActionsContext.displayName = 'ModalActionsContext';

const modalStateContextInitialValues: ModalStateContextType = {
    sideMenuModalList: {},
    isOpen: false,
    modalIdInView: undefined,
    isMenuModalOpen: () => false,
};

export const ModalStateContext = createContext<ModalStateContextType>(modalStateContextInitialValues);
ModalStateContext.displayName = 'ModalStateContext';
